import React from "react";
import { useTranslation } from "react-i18next";
import {
  Check, X, CheckCircle2, MapPin, Briefcase, Languages, Award, GraduationCap,
  CalendarClock, Star, Target, FileText, ExternalLink, MessageSquare, MailQuestion,
} from "lucide-react";
import { fileUrl } from "@/lib/api";
import { UserAvatar, StatusBadge, Pill, VerifiedBadge, Stars, Spinner } from "@/components/ui-bits";
import { SocialLinks } from "@/components/Socials";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";

function DetailRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon size={15} strokeWidth={1.75} className="text-muted-foreground shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground font-medium">{value}</span>
    </div>
  );
}

function DetailBlock({ title, icon: Icon, children }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-2 mb-2.5">
        {Icon && <Icon size={14} strokeWidth={1.75} />} {title}
      </h4>
      {children}
    </div>
  );
}

export function ExpertDetailSheet({ expert, onClose, onDecide, onRequestInfo, busy }) {
  const { t } = useTranslation();
  if (!expert) return null;
  const openSlots = (expert.availability || []).filter((s) => !s.booked && new Date(s.start) > new Date()).length;
  const isPending = expert.status === "pending";
  return (
    <Sheet open={!!expert} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col gap-0">
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 border-b border-border bg-gradient-to-b from-secondary/40 to-transparent">
            <div className="flex items-start gap-4">
              <UserAvatar src={fileUrl(expert.picture)} name={expert.name} size={64} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold tracking-tight">{expert.name}</h2>
                  {expert.verified ? <VerifiedBadge /> : <StatusBadge status={expert.status} />}
                  {expert.info_requested && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 text-warning px-2.5 py-0.5 text-xs font-medium">
                      <MailQuestion size={12} /> {t("admin.infoRequested")}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{expert.title || "—"}</p>
                <div className="mt-2"><Stars value={expert.rating_avg} count={expert.rating_count} size={14} /></div>
              </div>
            </div>
            {expert.headline && <p className="text-sm text-foreground mt-4 leading-relaxed">{expert.headline}</p>}
          </div>

          <div className="p-6 space-y-6">
            <div className="rounded-xl border border-border bg-secondary/30 p-4 space-y-2.5">
              <DetailRow icon={MapPin} label={t("admin.sheetLocation")} value={expert.location || t("admin.remote")} />
              <DetailRow icon={Briefcase} label={t("admin.sheetExperience")} value={expert.years_experience ? t("admin.years", { count: expert.years_experience }) : null} />
              <DetailRow icon={Languages} label={t("admin.sheetLanguages")} value={(expert.languages || []).join(", ") || null} />
              <DetailRow icon={CalendarClock} label={t("admin.sheetOpenSlots")} value={t("admin.available", { count: openSlots })} />
            </div>

            {(expert.fields || []).length > 0 && (
              <DetailBlock title={t("admin.focusAreas")} icon={Target}>
                <div className="flex flex-wrap gap-1.5">{expert.fields.map((f) => <Pill key={f} className="bg-primary/10 text-primary">{f}</Pill>)}</div>
              </DetailBlock>
            )}

            {expert.bio && (
              <DetailBlock title={t("admin.about")}>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{expert.bio}</p>
              </DetailBlock>
            )}

            {(expert.expertise || []).length > 0 && (
              <DetailBlock title={t("admin.expertise")}>
                <div className="flex flex-wrap gap-1.5">{expert.expertise.map((x) => <Pill key={x} className="bg-secondary">{x}</Pill>)}</div>
              </DetailBlock>
            )}

            {(expert.certifications || []).length > 0 && (
              <DetailBlock title={t("admin.certifications")} icon={GraduationCap}>
                <ul className="space-y-1.5">{expert.certifications.map((c) => <li key={c} className="text-sm text-muted-foreground flex gap-2"><Check size={15} className="text-success shrink-0 mt-0.5" /> {c}</li>)}</ul>
              </DetailBlock>
            )}

            {(expert.achievements || []).length > 0 && (
              <DetailBlock title={t("admin.achievements")} icon={Award}>
                <ul className="space-y-1.5">{expert.achievements.map((a) => <li key={a} className="text-sm text-muted-foreground flex gap-2"><Award size={15} className="text-warning shrink-0 mt-0.5" /> {a}</li>)}</ul>
              </DetailBlock>
            )}

            <DetailBlock title={t("admin.cv")} icon={FileText}>
              {expert.cv_url ? (
                <a href={fileUrl(expert.cv_url)} target="_blank" rel="noreferrer" data-testid="sheet-cv"
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-3.5 py-2.5 text-sm font-medium hover:border-primary/40 hover:text-primary transition-colors">
                  <span className="grid place-items-center w-8 h-8 rounded-md bg-primary/10 text-primary"><FileText size={16} /></span>
                  {expert.cv_name || t("admin.viewCvDoc")} <ExternalLink size={13} className="text-muted-foreground" />
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">{t("admin.noCv")}</p>
              )}
            </DetailBlock>

            <DetailBlock title={t("admin.socials")}>
              {expert.socials && Object.values(expert.socials).some(Boolean)
                ? <SocialLinks socials={expert.socials} />
                : <p className="text-sm text-muted-foreground">{t("admin.noLinks")}</p>}
            </DetailBlock>

            {(expert.reviews || []).length > 0 && (
              <DetailBlock title={t("admin.reviews", { count: expert.reviews.length })} icon={Star}>
                <div className="space-y-2">
                  {expert.reviews.map((r, i) => (
                    <div key={`${r.name}-${i}`} className="rounded-lg border border-border p-3">
                      <div className="flex items-center gap-2"><Stars value={r.rating} size={12} /><span className="text-sm font-medium">{r.name}</span></div>
                      {r.comment && <p className="text-sm text-muted-foreground mt-1">"{r.comment}"</p>}
                    </div>
                  ))}
                </div>
              </DetailBlock>
            )}
          </div>
        </div>

        <div className="border-t border-border p-4 bg-card">
          {isPending ? (
            <div className="space-y-2">
              <Button data-testid="sheet-request-info" variant="outline" disabled={busy} onClick={() => onRequestInfo(expert)}
                className="w-full rounded-lg gap-1.5">
                <MessageSquare size={16} /> {t("admin.requestMoreInfo")}
              </Button>
              <div className="flex gap-2">
                <Button data-testid="sheet-reject" variant="outline" disabled={busy} onClick={() => onDecide(expert.expert_id, "reject")} className="flex-1 rounded-lg gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/5">
                  <X size={16} /> {t("admin.reject")}
                </Button>
                <Button data-testid="sheet-verify" disabled={busy} onClick={() => onDecide(expert.expert_id, "approve")} className="flex-1 rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
                  {busy ? <Spinner /> : <><Check size={16} /> {t("admin.verifyExpert")}</>}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">{t("admin.rejectHint")}</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              {expert.status === "approved" ? <><CheckCircle2 size={16} className="text-success" /> {t("admin.verifiedNote")}</> : <><X size={16} className="text-destructive" /> {t("admin.rejectedNote")}</>}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
